




































































































































































































































import crypto from 'crypto-js';
import Vue from 'vue';
import { mapState } from 'vuex';
import {
  HubButton,
  HubModal,
  HubCard,
  HubIcon,
  HubInput,
  HubSelect,
} from '@/components/HubComponents';
import CreditCard from '@/components/Invoice/CreditCard.vue';
import invoiceServices from '@/services/hub/invoice';

interface Taxes {
    taxedValue: number;
    taxedValueCharged: number;
    taxedValuePartial: number;
    taxTotal: number;
}

interface Installments {
  text: string;
  value: number;
}

enum PERIOD {
  /** @returns `12` */
  ANNUAL = 12,
  /** Biannual (it occurs once every two years)
   * @returns `24`
  */
  BIANNUAL = 24,
  /** @returns `1` */
  MONTHLY = 1,
  /** @returns `3` */
  QUARTERLY = 3,
  /** @returns `6` */
  SEMESTER = 6,
}

export default Vue.extend({
  name: 'ViewInvoice',

  components: {
    CreditCard,
    HubButton,
    HubCard,
    HubIcon,
    HubInput,
    HubSelect,
    HubModal,
  },

  props: {
    invoiceHash: {
      type: String,
      default: '',
    },
    prospectId: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      activateCSSTransition: false,
      cardFlag: '',
      fields: {
        cardCvv: 'credit-card-cvv',
        cardMonth: 'credit-card-month',
        cardName: 'credit-card-name',
        cardNumber: 'credit-card-number',
        cardYear: 'credit-card-year',
      } as Components.CreditCard.Fields,
      finishPaymentButtonIsLoading: false,
      formData: {
        cardCvv: '',
        cardMonth: '',
        cardName: '',
        cardNumber: '',
        cardYear: '',
      } as Components.CreditCard.Labels,
      installments: [] as Installments[],
      invoice: {
        prospect: {},
        prospectInvoice: {
          value: '',
        },
        financialTaxes: {},
      } as Services.Invoice.Response.InvoiceData,
      originalValue: 0,
      taxesData: {
        taxedValue: 0,
        taxedValueCharged: 0,
        taxedValuePartial: 0,
        taxTotal: 0,
      },
      installmentsTaxesText: '',
      invoiceIsPaid: false,
      numberOfSelectedInstallments: 0,
      paymentApproved: false,
      paymentModal: false,
      placeholder: '',
      years: [] as string[],
    };
  },

  computed: {
    ...mapState(['user']),
    finishPaymentButtonIsDisabled(): boolean {
      return !(
        (
          this.formData.cardNumber.replace(/\s/g, '').length
          === this.placeholder.replace(/\s/g, '').length
        )
        && !!this.formData.cardName.trim()
        && !!this.formData.cardMonth.trim()
        && !!this.formData.cardYear.trim()
        && !!this.formData.cardCvv.trim()
        && this.numberOfSelectedInstallments > 0
      );
    },
    userState(): Store.User.State {
      return this.user;
    },
    creditCardAdditionValue(): number {
      const planSum = this.invoice.prospectInvoice.plans.reduce(
        (acc: number, plan: Services.Invoice.Plan) => acc + parseFloat(plan.price), 0,
      );
      return parseFloat(this.invoice.prospectInvoice.value) - planSum;
    },
  },

  watch: {
    numberOfSelectedInstallments(): void {
      this.getCreditOrDebitTaxes(
        this.numberOfSelectedInstallments,
        this.originalValue,
      );
      this.setTaxedValues();
    },
  },

  async mounted() {
    // jmv-analytics saving visit on browser cookie
    setTimeout(() => {
      console.log('--- saving visit ---');
      const iframeAnalytics = document.createElement('iframe');
      iframeAnalytics.style.display = 'none';
      iframeAnalytics.setAttribute('id', 'jmvanalytics');
      iframeAnalytics.src = `${process.env.VUE_APP_JMV_ANALYTICS_API}/api/setcookie/${encodeURIComponent(window.location.href)}/ref=${encodeURIComponent(document.referrer)}`;
      document.body.appendChild(iframeAnalytics);
      iframeAnalytics.onload = () => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          iframeAnalytics.contentWindow!.postMessage('begin', '*');
      };
    }, 500);

    await this.getInvoiceData({
      invoiceHash: this.invoiceHash,
      prospectId: this.prospectId,
    })
      .then((invoiceData) => {
        if (invoiceData.prospectInvoice.situation === 'P') {
          this.invoiceIsPaid = true;
        }
        this.invoice = invoiceData;
      })
      .catch(() => {
        this.$router.push({
          name: 'Error',
          params: { error: '404' },
        });
      });
    this.originalValue = parseFloat(this.invoice.prospectInvoice.value);
    this.installments = this.getInstallments();
    this.getCreditOrDebitTaxes(
      0,
      this.originalValue,
    );
    this.setTaxedValues();
    const currentYear = new Date().getFullYear();

    for (let i = 0; i <= 19; i += 1) {
      this.years.push((currentYear + i).toString());
    }
  },

  methods: {
    encrypt(text: string): string {
      return crypto.AES.encrypt(text, String(process.env.VUE_APP_FINANCIAL_SECRET), {
        padding: crypto.pad.Pkcs7,
        mode: crypto.mode.CBC,
      }).toString();
    },
    async getCardFlag(cardNumber: string): Promise<string> {
      const payload = this.encrypt(cardNumber);

      return invoiceServices.getCardFlag(payload)
        .then(({ data }) => data.flag.toLowerCase())
        .catch((error) => {
          console.error('ViewInvoice - method: "getCardFlag()" ', error);

          return this.getCardFlagWithoutApi(cardNumber);
        });
    },
    getCardFlagWithoutApi(cardNumber: string): string {
      let re = new RegExp('^4');
      if (cardNumber.match(re) !== null) return 'visa';

      re = new RegExp('^(34|37)');
      if (cardNumber.match(re) !== null) return 'amex';

      re = new RegExp('^5[1-5]');
      if (cardNumber.match(re) !== null) return 'mastercard';

      re = new RegExp('^6011');
      if (cardNumber.match(re) !== null) return 'discover';

      re = new RegExp('^62');
      if (cardNumber.match(re) !== null) return 'unionpay';

      re = new RegExp('^9792');
      if (cardNumber.match(re) !== null) return 'troy';

      re = new RegExp('^3(?:0([0-5]|9)|[689]\\d?)\\d{0,11}');
      if (cardNumber.match(re) !== null) return 'dinersclub';

      re = new RegExp('^35(2[89]|[3-8])');
      if (cardNumber.match(re) !== null) return 'jcb';

      return '';
    },
    getEncryptedCardData(): Services.Invoice.Card {
      return {
        cardName: this.encrypt(this.formData.cardName),
        cardNumber: this.encrypt(this.formData.cardNumber.replace(/\s/g, '')),
        cvv: this.encrypt(this.formData.cardCvv.trim()),
        month: this.encrypt(this.formData.cardMonth),
        year: this.encrypt(this.formData.cardYear.slice(2)),
      };
    },
    setTaxedValues(): void {
      // set tax value and text to show fees to the user
      const { taxedValue, taxTotal } = this.taxesData;
      const parcel = taxedValue / this.numberOfSelectedInstallments;
      const totalFees = taxTotal > 0 ? `com juros total de ${taxTotal.toFixed(2)}%` : 'sem juros';
      this.invoice.prospectInvoice.value = taxedValue.toFixed(2);
      this.installmentsTaxesText = `${this.numberOfSelectedInstallments}x de ${parcel.toFixed(2)} ${totalFees}`;
      console.log('this.numberOfSelectedInstallments', this.numberOfSelectedInstallments);
      console.log('taxedValue', taxedValue);
    },
    calculateTaxValues(value: number, i: number, anticipation: number, cardTax: number): Taxes {
      return {
        taxedValue: value + ((i * anticipation + cardTax) / 100) * value,
        taxedValueCharged: ((i * anticipation + cardTax) / 100) * value,
        taxedValuePartial: ((cardTax) / 100) * value,
        taxTotal: (i * anticipation) + cardTax,
      };
    },
    getCreditOrDebitTaxes(installment: number, value: number): Taxes {
      const {
        credi_card_tax_anticipation: anticipation,
        credi_card_tax_credit: cardTaxCredit,
        credi_card_tax_credit_second: cardTaxCreditSecond,
        credi_card_tax_credit_third: cardTaxCreditThird,
        credi_card_tax_credit_fourth: cardTaxCreditFourth,
      } = this.invoice.financialTaxes;
      let data = {
        taxedValue: 0,
        taxedValueCharged: 0,
        taxedValuePartial: 0,
        taxTotal: 0,
      };
      if (installment === 1) {
        data.taxedValue = value;
        data.taxedValuePartial = ((cardTaxCredit) / 100) * value;
      } else if (installment >= 2 && installment <= 3) {
        data = this.calculateTaxValues(value, installment, anticipation, cardTaxCreditSecond);
      } else if (installment >= 4 && installment <= 6) {
        data = this.calculateTaxValues(value, installment, anticipation, cardTaxCreditThird);
      } else if (installment >= 7 && installment <= 12) {
        data = this.calculateTaxValues(value, installment, anticipation, cardTaxCreditFourth);
      } else {
        data.taxedValue = value;
      }
      this.taxesData = data;
      return data;
    },
    getInstallments(): Installments[] {
      let installments = 0;
      switch (this.invoice.prospectInvoice.period) {
        case 'A': installments = PERIOD.ANNUAL; break;
        case 'B': installments = PERIOD.BIANNUAL; break;
        case 'M': installments = PERIOD.MONTHLY; break;
        case 'Q': installments = PERIOD.QUARTERLY; break;
        case 'S': installments = PERIOD.SEMESTER; break;
        default: break;
      }

      return Array(installments).fill('').map((_, index) => {
        const numberOfInstallments = index + 1;
        const { taxedValue, taxTotal } = this.getCreditOrDebitTaxes(
          numberOfInstallments,
          this.originalValue,
        );
        const totalFeesText = taxTotal > 0 && numberOfInstallments !== 1 ? 'com juros' : 'sem juros';
        const installmentValue = this.toLocaleString(
          (taxedValue / numberOfInstallments).toFixed(2),
        );
        return {
          text: `${this.$t('installments', { numberOfInstallments, installmentValue })} ${totalFeesText}`,
          value: numberOfInstallments,
        };
      });
    },
    async getInvoiceData(
      payload: Services.Invoice.GetInvoiceData,
    ): Promise<Services.Invoice.Response.InvoiceData> {
      return invoiceServices.getInvoiceData(payload)
        .then(({ data }) => data.data)
        .catch((error) => {
          console.error('ViewInvoice - method: "getInvoiceData()" ', error);

          // this.showNotification('error_fetching_invoice_data', 'error');

          throw new Error(error);
        });
    },
    async getLocation(): Promise<Services.Invoice.Response.LocationData> {
      return invoiceServices.getLocation()
        .then(({ data }) => data.data)
        .catch((error) => {
          console.error('ViewInvoice - method: "getLocation()" ', error);

          // this.showNotification('__Erro ao buscar dados de localização', 'error');

          throw new Error(error);
        });
    },
    async getPostPaymentPayload(): Promise<Services.Invoice.PostPayment> {
      const locationResponse = await this.getLocation()
        .catch((error) => { throw new Error(error); });

      const location = {
        city: locationResponse.city,
        continent: locationResponse.continent,
        country: locationResponse.country,
        country_dialcode: ' 55',
        country_isocode: locationResponse.API.country.iso_code,
        latitude: locationResponse.latitude,
        longitude: locationResponse.longitude,
        state_province: locationResponse.state_province,
        time_zone: locationResponse.time_zone,
      } as Services.Invoice.PostPaymentLocation;

      const {
        credi_card_account: account,
        credi_card_cent_cost_cat: costCat,
      } = this.invoice.financialTaxes;

      const payment = {
        card: this.getEncryptedCardData(),
        currency: this.userState.settings.lang === 'pt-BR' ? 'R$' : '$',
        installments: this.numberOfSelectedInstallments,
        taxesData: {
          taxed_value_charged: this.taxesData.taxedValueCharged,
          taxed_value_partial: this.taxesData.taxedValuePartial,
          credi_card_account: account,
          credi_card_cent_cost_cat: costCat,
        },
        original_value: this.originalValue,
        value: parseFloat(this.invoice.prospectInvoice.value),
      } as Services.Invoice.Payment;

      return {
        location,
        payment,
        plans: this.invoice.prospectInvoice.plans,
        prospect: this.invoice.prospect,
        prospectInvoice: { hash: this.invoice.prospectInvoice.hash },
      };
    },
    inputCardName(value: string) {
      const regex = /[^a-zA-Z\s]/g;

      if (regex.test(value)) {
        this.formData.cardName = this.formData.cardName.replace(regex, '');
      }

      this.formData.cardName = this.formData.cardName.toUpperCase().trim();
    },
    async inputCardNumber(inputValue: string) {
      let currentPlaceholder = this.placeholder;
      let value = inputValue;
      const valueWithoutSpaces = value.replace(/\s/g, '');

      if (/\D/g.test(value)) {
        value = value.replace(/\D/g, '');
      }

      // eslint-disable-next-line no-restricted-syntax
      for (const char of value) {
        currentPlaceholder = currentPlaceholder.replace('#', char);
      }

      this.formData.cardNumber = currentPlaceholder.replace(/#/g, '').trim();

      if (valueWithoutSpaces.length === 6 || valueWithoutSpaces.length >= 15) {
        this.cardFlag = await this.getCardFlag(valueWithoutSpaces);
      }
    },
    async onClickFinishPayment() {
      console.log('-------------');
      console.log(this.invoice.prospect);
      const iframeAnalyticsCollector = document.createElement('iframe');
      console.log('---- collecting -----');
      iframeAnalyticsCollector.style.display = 'none';
      iframeAnalyticsCollector.setAttribute('id', 'jmvanalyticscollector');
      iframeAnalyticsCollector.src = `${process.env.VUE_APP_JMV_ANALYTICS_API}/api/setcookie/saving/UUID-${this.invoice.prospect.id}`;
      console.log(iframeAnalyticsCollector.src);
      document.body.appendChild(iframeAnalyticsCollector);
      iframeAnalyticsCollector.onload = () => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        iframeAnalyticsCollector.contentWindow!.postMessage('retrieve', '*');
      };

      this.finishPaymentButtonIsLoading = true;

      const payload = await this.getPostPaymentPayload()
        .catch(() => 'no_data') as Services.Invoice.PostPayment | 'no_data';

      if (payload === 'no_data') {
        this.finishPaymentButtonIsLoading = false;
        return;
      }

      await invoiceServices.postPayment(payload)
        .then(() => {
          this.paymentApproved = true;
        })
        .catch((error) => {
          console.error('ViewInvoice - method: "postPayment()" ', error);

          // this.showNotification('');
        })
        .finally(() => {
          this.paymentModal = true;
        });

      this.finishPaymentButtonIsLoading = false;
    },
    closePaymentResponseModal() {
      this.paymentModal = false;

      if (this.paymentApproved) {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    },
    showNotification(text: string, type: 'error' | 'success'): void {
      this.$store.dispatch('notification/create', { text, type });
    },
    toLocaleString(value: number | string): string {
      const parsedValue = typeof value === 'string'
        ? parseFloat(value)
        : value;

      return parsedValue.toLocaleString(
        this.userState.settings.lang, {
          style: 'currency',
          currency: this.userState.settings.lang === 'pt-BR' ? 'BRL' : 'USD',
        },
      );
    },
  },
});
